// Feriados usados pela régua de prazos (#5787 — cadastro editável, pedido de 16/09/2026).
//
// A régua já calcula os nacionais, os móveis (Carnaval, Sexta-feira Santa, Corpus Christi), o
// estadual de Goiás e o municipal de Morrinhos. Esta tela serve para o que o cálculo não sabe:
// acrescentar um feriado (ponto facultativo decretado, feriado municipal novo) ou SUPRIMIR um
// padrão que não existe — o municipal entrou no código como "a confirmar".
//
// Tabela vazia = régua idêntica à de antes.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import ConfirmModal from '../../src/components/ConfirmModal';
import InfoTip from '../../src/components/InfoTip';
import { feriadosDoAno, type Feriado } from '../../src/lib/reguaPrazosAgrupamento';
import type { FeriadoCadastro } from '../../types';

const CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200';

const dataBr = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

interface Props {
  /** Recarrega a aba mensal quando o cadastro muda: a régua depende dele. */
  onMudou?: () => void;
}

const FeriadosAgrupamentoPanel: React.FC<Props> = ({ onMudou }) => {
  const [ano, setAno] = useState(new Date().getFullYear());
  const [cadastrados, setCadastrados] = useState<FeriadoCadastro[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [apagando, setApagando] = useState<FeriadoCadastro | null>(null);

  const [novaData, setNovaData] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [novoTipo, setNovoTipo] = useState<'inclui' | 'exclui'>('inclui');
  const [novaAbrangencia, setNovaAbrangencia] = useState<'nacional' | 'estadual' | 'municipal'>('municipal');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data, error } = await supabase
        .from('feriados')
        .select('*')
        .gte('data', `${ano}-01-01`)
        .lte('data', `${ano}-12-31`)
        .order('data');
      if (error) throw error;
      setCadastrados((data ?? []) as FeriadoCadastro[]);
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível carregar os feriados.');
    } finally {
      setCarregando(false);
    }
  }, [ano]);

  useEffect(() => { carregar(); }, [carregar]);

  /** Calendário final do ano: o que o código calcula, mais o cadastro, menos o que foi suprimido. */
  const calendario = useMemo(() => {
    const padroes: Feriado[] = feriadosDoAno(ano);
    const porData = new Map<string, { data: string; nome: string; origem: 'padrão' | 'cadastrado'; suprimido?: boolean; id?: number }>();
    for (const f of padroes) porData.set(f.data, { data: f.data, nome: f.nome, origem: 'padrão' });
    for (const c of cadastrados) {
      const d = String(c.data).slice(0, 10);
      if (c.tipo === 'exclui') {
        const atual = porData.get(d);
        porData.set(d, { data: d, nome: atual?.nome ?? c.nome, origem: atual ? 'padrão' : 'cadastrado', suprimido: true, id: c.id });
      } else {
        porData.set(d, { data: d, nome: c.nome, origem: 'cadastrado', id: c.id });
      }
    }
    return [...porData.values()].sort((a, b) => a.data.localeCompare(b.data));
  }, [ano, cadastrados]);

  const salvar = async () => {
    if (!novaData || !novoNome.trim()) return;
    setGravando(true);
    setErro(null);
    try {
      const { error } = await supabase.from('feriados').upsert({
        data: novaData,
        nome: novoNome.trim(),
        tipo: novoTipo,
        abrangencia: novaAbrangencia,
      }, { onConflict: 'data' });
      if (error) throw error;
      setAviso(novoTipo === 'inclui'
        ? `${dataBr(novaData)} passa a contar como feriado.`
        : `${dataBr(novaData)} deixa de contar como feriado.`);
      setNovaData(''); setNovoNome('');
      await carregar();
      onMudou?.();
    } catch (e: any) {
      setErro(e?.code === '42501' ? 'Seu usuário não pode alterar os feriados.' : (e?.message || 'Não foi possível salvar.'));
    } finally {
      setGravando(false);
    }
  };

  const apagar = async () => {
    if (!apagando) return;
    setGravando(true);
    setErro(null);
    try {
      const { error } = await supabase.from('feriados').delete().eq('id', apagando.id);
      if (error) throw error;
      setAviso('Registro removido: a data volta a valer pelo cálculo padrão.');
      setApagando(null);
      await carregar();
      onMudou?.();
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível remover.');
    } finally {
      setGravando(false);
    }
  };

  const anos = useMemo(() => {
    const atual = new Date().getFullYear();
    return [atual - 1, atual, atual + 1, atual + 2];
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
          <CalendarDays size={16} className="text-primary" />
          Feriados da régua de prazos
          <InfoTip label="Para que serve">
            <p className="mb-2">
              A régua calcula sozinha os feriados nacionais, os móveis, o estadual de Goiás e o
              municipal de Morrinhos. Aqui você <strong>acrescenta</strong> o que faltar e
              <strong> remove</strong> o que não vale naquele ano.
            </p>
            <p>Sem nenhum cadastro, a conta fica exatamente como já era.</p>
          </InfoTip>
        </h2>
        <label className="sr-only" htmlFor="feriados-ano">Ano</label>
        <select id="feriados-ano" value={ano} onChange={e => setAno(Number(e.target.value))} className={CAMPO}>
          {anos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button onClick={carregar} disabled={carregando} aria-busy={carregando}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
          <RefreshCw size={15} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {erro && (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">{erro}</p>
      )}
      {aviso && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300" role="status">{aviso}</p>
      )}

      <div className="rounded-2xl border border-gray-200 p-3 dark:border-gray-700">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Acrescentar ou remover</p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="feriado-data">Data</label>
            <input id="feriado-data" type="date" value={novaData} onChange={e => setNovaData(e.target.value)} className={CAMPO} />
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="feriado-nome">Nome</label>
            <input id="feriado-nome" value={novoNome} onChange={e => setNovoNome(e.target.value)}
              placeholder="Ex.: Aniversário de Morrinhos" className={`${CAMPO} w-full`} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="feriado-tipo">O que fazer</label>
            <select id="feriado-tipo" value={novoTipo} onChange={e => setNovoTipo(e.target.value as 'inclui' | 'exclui')} className={CAMPO}>
              <option value="inclui">É feriado</option>
              <option value="exclui">Não é feriado</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="feriado-abrangencia">Abrangência</label>
            <select id="feriado-abrangencia" value={novaAbrangencia} onChange={e => setNovaAbrangencia(e.target.value as any)} className={CAMPO}>
              <option value="municipal">Municipal</option>
              <option value="estadual">Estadual</option>
              <option value="nacional">Nacional</option>
            </select>
          </div>
          <button type="button" onClick={salvar} disabled={!novaData || !novoNome.trim() || gravando}
            aria-busy={gravando}
            title={!novaData ? 'Escolha a data' : !novoNome.trim() ? 'Informe o nome' : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
            {gravando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Salvar
          </button>
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Já existe registro nessa data? O novo substitui o antigo.
        </p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Feriado</th>
              <th className="px-3 py-2">Origem</th>
              <th className="px-3 py-2">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {calendario.map(f => (
              <tr key={f.data} className={f.suprimido ? 'opacity-60' : ''}>
                <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-white">{dataBr(f.data)}</td>
                <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-200">
                  {f.nome}
                  {f.suprimido && <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">não conta como feriado</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{f.origem}</td>
                <td className="px-3 py-2">
                  {f.id ? (
                    <button type="button" onClick={() => setApagando(cadastrados.find(c => c.id === f.id) ?? null)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                      <Trash2 size={13} /> Remover
                    </button>
                  ) : <span className="text-xs text-gray-400">calculado</span>}
                </td>
              </tr>
            ))}
            {calendario.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Nenhum feriado em {ano}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={!!apagando}
        onClose={() => { if (!gravando) setApagando(null); }}
        onConfirm={apagar}
        title="Remover registro de feriado"
        message={apagando
          ? `${dataBr(String(apagando.data).slice(0, 10))} volta a valer pelo cálculo padrão da régua.`
          : ''}
        confirmLabel={gravando ? 'Removendo...' : 'Remover'}
        loading={gravando}
        variant="danger"
      />
    </div>
  );
};

export default FeriadosAgrupamentoPanel;
